import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Package, FolderTree, Building2, Images, Inbox, LogOut, ExternalLink } from "lucide-react";
import { API, setToken, clearToken, getToken, api, fmtErr } from "./api";
import { CategoriesTab } from "./CategoriesTab";
import { ProductsTab } from "./ProductsTab";
import { ProjectsTab } from "./ProjectsTab";
import { GalleryTab } from "./GalleryTab";
import { InquiriesTab } from "./InquiriesTab";

const TABS = [
    { id: "products", label: "Products", desc: "Add & edit items", icon: Package },
    { id: "categories", label: "Categories", desc: "Manage sections", icon: FolderTree },
    { id: "projects", label: "Projects", desc: "Client work photos", icon: Building2 },
    { id: "gallery", label: "Gallery", desc: "Factory photos", icon: Images },
    { id: "inquiries", label: "Inquiries", desc: "Customer leads", icon: Inbox },
];

const LoginScreen = ({ onLogin }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            const { data } = await axios.post(`${API}/admin/login`, { email, password });
            setToken(data.token);
            onLogin(data);
        } catch (er) {
            setErr(fmtErr(er));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-bone flex items-center justify-center p-6" data-testid="admin-login-screen">
            <form onSubmit={submit} className="w-full max-w-md bg-white border border-line p-8 md:p-10" data-testid="admin-login-form">
                <p className="font-heading font-extrabold tracking-tight text-2xl text-navy">ZAM ZAM FURNITURES</p>
                <p className="font-mono text-[10px] tracking-[0.3em] text-gold mt-1 mb-8">ADMIN PANEL · TASKLANCE®</p>
                <label className="block text-sm font-semibold text-navy mb-2">Your Email</label>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full border border-line px-4 py-4 text-lg outline-none focus:border-gold mb-5"
                    placeholder="admin@zzfurnitures.com" data-testid="admin-email-input" />
                <label className="block text-sm font-semibold text-navy mb-2">Your Password</label>
                <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                    className="w-full border border-line px-4 py-4 text-lg outline-none focus:border-gold mb-6"
                    placeholder="••••••••" data-testid="admin-password-input" />
                {err && <p className="text-red-600 text-sm mb-4" data-testid="admin-login-error">{err}</p>}
                <button type="submit" disabled={busy}
                    className="w-full bg-navy text-white py-4 text-lg font-semibold hover:bg-gold transition-colors disabled:opacity-50"
                    data-testid="admin-login-button">
                    {busy ? "Checking..." : "Login to Admin Panel"}
                </button>
                <a href="/" className="block text-center text-sm text-muted mt-6 hover:text-gold" data-testid="back-to-website-link">
                    ← Back to website
                </a>
            </form>
        </div>
    );
};

const AdminApp = () => {
    const [user, setUser] = useState(null); // null = checking
    const [tab, setTab] = useState("products");

    useEffect(() => {
        if (!getToken()) {
            setUser(false);
            return;
        }
        api()
            .get("/admin/me")
            .then((res) => setUser(res.data))
            .catch(() => {
                clearToken();
                setUser(false);
            });
    }, []);

    if (user === null) {
        return <div className="min-h-screen bg-bone flex items-center justify-center text-muted">Loading...</div>;
    }
    if (user === false) {
        return <LoginScreen onLogin={setUser} />;
    }

    const logout = () => {
        clearToken();
        setUser(false);
        toast.success("Logged out safely");
    };

    return (
        <div className="min-h-screen bg-bone" data-testid="admin-dashboard">
            <header className="bg-navy text-white">
                <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-5 flex items-center justify-between gap-4">
                    <div>
                        <p className="font-heading font-extrabold tracking-tight text-xl">ZZ ADMIN PANEL</p>
                        <p className="font-mono text-[10px] tracking-[0.25em] text-gold">TASKLANCE® · WEBSITE MANAGER</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <a href="/" target="_blank" rel="noopener noreferrer"
                            className="hidden sm:inline-flex items-center gap-2 border border-white/30 px-4 py-2.5 text-sm font-medium hover:border-gold hover:text-gold transition-colors"
                            data-testid="view-website-button">
                            <ExternalLink size={15} /> View Website
                        </a>
                        <button onClick={logout}
                            className="inline-flex items-center gap-2 bg-white/10 px-4 py-2.5 text-sm font-medium hover:bg-gold transition-colors"
                            data-testid="admin-logout-button">
                            <LogOut size={15} /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-8">
                <p className="text-muted text-sm mb-6" data-testid="admin-welcome">
                    Welcome! Everything you change here appears on your live website immediately. No technical knowledge needed.
                </p>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8" data-testid="admin-tabs">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`admin-tab-${t.id}`}
                            className={`p-4 md:p-5 border text-left transition-colors ${
                                tab === t.id ? "bg-navy text-white border-navy" : "bg-white border-line hover:border-gold"
                            }`}
                        >
                            <t.icon size={26} className={tab === t.id ? "text-gold" : "text-navy"} />
                            <p className="font-heading font-bold text-lg mt-2 tracking-tight">{t.label}</p>
                            <p className={`text-xs mt-0.5 ${tab === t.id ? "text-slate-300" : "text-muted"}`}>{t.desc}</p>
                        </button>
                    ))}
                </div>

                <div className="bg-white border border-line p-4 md:p-8 min-h-[400px]" data-testid="admin-content">
                    {tab === "products" && <ProductsTab />}
                    {tab === "categories" && <CategoriesTab />}
                    {tab === "projects" && <ProjectsTab />}
                    {tab === "gallery" && <GalleryTab />}
                    {tab === "inquiries" && <InquiriesTab />}
                </div>
            </div>
        </div>
    );
};

export default AdminApp;
